<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            component: {
                dashboard_iot_header: {
                    waiting_message:
                        "L'analyse est en cours de traitement, veuillez revenir dans quelques minutes.",
                    reload: "Vérifier l'état de l'analyse",
                    start_synthesis: "Lancer la synthèse",
                    invalid_dates:
                        "La date de début doit être antérieure à la date de fin",
                    start_date_label: "Date de début",
                    end_date_label: "Date de fin",
                    finished_tasks_message:
                        "Depuis la dernière vérification {nbFinished} bâtiment(s) traité(s) sur {total} bâtiment(s).",
                },
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        canCreateNewAnalyse?: boolean
        dateFrom?: string | null
        dateTo?: string | null
        isValidDates?: boolean
        loadingStartAnalyse?: boolean
        requiredWaiting?: boolean
        status?: string
        /** Nombre de tâches terminées (bâtiments traités) */
        nbFinishedTasks?: number
        /** Nombre total d'analyses (bâtiments dans le périmètre) */
        analysesCount?: number
    }>(),
    {
        canCreateNewAnalyse: false,
        dateFrom: null,
        dateTo: null,
        isValidDates: true,
        loadingStartAnalyse: false,
        requiredWaiting: false,
        status: "",
        nbFinishedTasks: 0,
        analysesCount: 0,
    }
)

const emit = defineEmits<{
    "update:startDate": [date: string | null]
    "update:endDate": [date: string | null]
    "click:start-synthesis": []
    "click:reload": []
}>()

// ================================
// DONNÉES CALCULÉES
// ================================
const getShowAlert = computed(() => props.requiredWaiting)

const showAlertReloadButton = computed(() => props.requiredWaiting)

/** Affiche le message de progression (bâtiments traités / total) uniquement quand l'analyse est en PENDING */
const getShowFinishedTasksMessage = computed(
    () => props.status === "PENDING" && props.analysesCount > 0
)

// ================================
// INTERACTIONS
// ================================
function onUpdateStartDate(date: Date | null): void {
    emit("update:startDate", date ? moment(date).format("YYYY-MM-DD") : null)
}

function onUpdateEndDate(date: Date | null): void {
    emit("update:endDate", date ? moment(date).format("YYYY-MM-DD") : null)
}
</script>

<template>
    <BaseAppMessage
        v-if="getShowAlert"
        severity="info"
        :action="
            showAlertReloadButton
                ? {
                      message: t('component.dashboard_iot_header.reload'),
                      fn: () => emit('click:reload'),
                  }
                : undefined
        "
    >
        <p>
            {{
                t("component.dashboard_iot_header.waiting_message", {
                    status: status || "",
                })
            }}
        </p>
        <p v-if="getShowFinishedTasksMessage">
            {{
                t("component.dashboard_iot_header.finished_tasks_message", {
                    nbFinished: nbFinishedTasks,
                    total: analysesCount,
                })
            }}
        </p>
    </BaseAppMessage>

    <div v-if="canCreateNewAnalyse" class="flex flex-col gap-4 w-full">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div class="flex flex-col gap-2">
                <label
                    for="start-date"
                    class="text-sm font-medium text-gray-700"
                >
                    {{ t("component.dashboard_iot_header.start_date_label") }}
                </label>
                <BaseDatePicker
                    id="start-date"
                    :model-value="
                        dateFrom
                            ? moment(dateFrom, 'YYYY-MM-DD').toDate()
                            : null
                    "
                    date-format="dd/mm/yy"
                    show-icon
                    :max-date="moment().add(1, 'day').toDate()"
                    :invalid="!isValidDates"
                    @update:model-value="onUpdateStartDate"
                />
            </div>

            <div class="flex flex-col gap-2">
                <label for="end-date" class="text-sm font-medium text-gray-700">
                    {{ t("component.dashboard_iot_header.end_date_label") }}
                </label>
                <BaseDatePicker
                    id="end-date"
                    :model-value="
                        dateTo ? moment(dateTo, 'YYYY-MM-DD').toDate() : null
                    "
                    date-format="dd/mm/yy"
                    show-icon
                    :max-date="moment().add(1, 'day').toDate()"
                    :invalid="!isValidDates"
                    @update:model-value="onUpdateEndDate"
                />
            </div>
        </div>

        <BaseButton
            :label="t('component.dashboard_iot_header.start_synthesis')"
            :loading="loadingStartAnalyse"
            :disabled="!isValidDates || loadingStartAnalyse"
            class="w-auto self-center"
            @click="emit('click:start-synthesis')"
        />
    </div>

    <BaseMessage
        v-if="canCreateNewAnalyse && !isValidDates"
        severity="error"
        class="w-full"
    >
        <p>{{ t("component.dashboard_iot_header.invalid_dates") }}</p>
    </BaseMessage>
</template>
